// RTK Query endpoints for the badge admin page. The engine switch itself goes
// through the scheduler admin API (services/rtkApi/schedulerAdminApi.ts).
import { baseApi } from "../../../services/rtkApi/baseApi";
import type {
  AdminBadgeDoc,
  AdminHolderDoc,
  AdminRankingResponse,
  AdminSweepRequest,
  AdminSweepResult,
} from "./adminTypes";

/** The scheduler task that is also the badge engine's master switch. */
export const BADGE_ENGINE_TASK = "badge-engine";

export const adminBadgePaths = {
  defs: "/badges/admin/badges",
  holders: (code: string) => `/badges/admin/badge-users/${encodeURIComponent(code)}`,
  sweep: "/badges/admin/sweep",
  ranking: "/badges/admin/ranking",
  uploadCsv: "/api/badges/admin/upload-badges",
  uploadImage: "/api/badges/admin/upload-badge-image",
} as const;

export const adminBadgesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminBadgeDefs: builder.query<AdminBadgeDoc[], void>({
      query: () => adminBadgePaths.defs,
      providesTags: ["Badges"],
    }),
    getAdminBadgeHolders: builder.query<AdminHolderDoc[], string>({
      query: (code) => adminBadgePaths.holders(code),
      providesTags: ["Badges"],
    }),
    // Everyone's badge rank. Tagged Badges, so a real sweep refetches it.
    getAdminBadgeRanking: builder.query<AdminRankingResponse, void>({
      query: () => adminBadgePaths.ranking,
      providesTags: ["Badges"],
    }),
    // A dry run writes nothing. A real run writes copies and refreshes every
    // definition's holder counts, so it refetches the catalogue.
    runAdminBadgeSweep: builder.mutation<AdminSweepResult, AdminSweepRequest>({
      query: (body) => ({ url: adminBadgePaths.sweep, method: "POST", body }),
      invalidatesTags: (_result, _error, arg) => (arg.dryRun ? [] : ["Badges"]),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminBadgeDefsQuery,
  useGetAdminBadgeHoldersQuery,
  useGetAdminBadgeRankingQuery,
  useRunAdminBadgeSweepMutation,
} = adminBadgesApi;
